import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { Card, CardHeader } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { TextArea } from '../components/ui/TextArea';
import { Table } from '../components/ui/Table';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { Checkbox } from '../components/ui/Checkbox';
import { Select } from '../components/ui/Select';
import { TermsAndConditions } from '../types';
import { formatDate } from '../utils/calculations';
import { formatCurrency } from '../utils/formatters';
import { callBillingAPI } from '../utils/api';
import { SETTINGS_RESET_EVENT } from '../components/layout/Layout';
import { CustomerSelect } from '../components/domain/CustomerSelect';
import { BuildingIcon, PlusIcon, EditIcon, TrashIcon, FileTextIcon, AlertCircleIcon, UploadIcon, XIcon, PercentIcon, ArrowLeftIcon, MailIcon, MessageSquareIcon, SettingsIcon, Share2Icon, RefreshCwIcon, RepeatIcon, SaveIcon, PlayIcon, CheckCircleIcon, CreditCardIcon, XCircleIcon } from 'lucide-react';
interface ApiCompany {
  id: string;
  name: string;
  email: string;
  phone: string;
  alias: string;
  stripe_private_key: string;
  stripe_public_key: string;
  logo?: string;
  tax_rate?: number;
  invoice_email_template?: string;
  invoice_email_subject_template?: string;
  invoice_sms_template?: string;
}
interface CompanyData {
  id: string;
  name: string;
  email: string;
  phone: string;
  alias: string;
  stripe_public_key: string;
  stripe_private_key: string;
  logo?: string;
  tax_rate?: number;
  invoice_email_template?: string;
  invoice_email_subject_template?: string;
  invoice_sms_template?: string;
}
type EditCompanyTab = 'general' | 'templates' | 'subscription_templates';
export function Settings() {
  const location = useLocation();
  const {
    selectedCompanyId,
    getTermsByCompanyId,
    createTerms,
    updateTerms,
    deleteTerms,
    refreshCompanies
  } = useData();
  const [companies, setCompanies] = useState<CompanyData[]>([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(true);
  const [companiesError, setCompaniesError] = useState<string | null>(null);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [editingTerms, setEditingTerms] = useState<TermsAndConditions | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  // Company edit state (inline, not modal)
  const [editingCompany, setEditingCompany] = useState<CompanyData | null>(null);
  const [activeTab, setActiveTab] = useState<EditCompanyTab>('general');
  const [companyName, setCompanyName] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');
  const [companyStripePublicKey, setCompanyStripePublicKey] = useState('');
  const [companyStripePrivateKey, setCompanyStripePrivateKey] = useState('');
  const [companyTaxRate, setCompanyTaxRate] = useState<number>(0);
  const [enterNewPrivateKey, setEnterNewPrivateKey] = useState(false);
  const [companyErrors, setCompanyErrors] = useState<Record<string, string>>({});
  const [isSavingCompany, setIsSavingCompany] = useState(false);
  // Logo state
  const [companyLogo, setCompanyLogo] = useState<string | null>(null);
  const [logoChanged, setLogoChanged] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  // Template state - we use a ref to avoid re-rendering issues with contentEditable
  const [invoiceEmailTemplate, setInvoiceEmailTemplate] = useState('');
  const [invoiceEmailSubjectTemplate, setInvoiceEmailSubjectTemplate] = useState('');
  const [invoiceSmsTemplate, setInvoiceSmsTemplate] = useState('');
  // Rich text editor ref
  const emailEditorRef = useRef<HTMLDivElement>(null);
  const editorInitializedRef = useRef(false);
  // Link tooltip state
  const [linkTooltip, setLinkTooltip] = useState<{
    url: string;
    x: number;
    y: number;
  } | null>(null);
  // Subscription templates state
  interface SubscriptionTemplate {
    id: string;
    name: string;
    cycle: string;
    cycle_num: number;
    amount: number;
    link: string;
  }
  const [subscriptionTemplates, setSubscriptionTemplates] = useState<SubscriptionTemplate[]>([]);
  const [isLoadingSubTemplates, setIsLoadingSubTemplates] = useState(false);
  const [subTemplatesError, setSubTemplatesError] = useState<string | null>(null);
  // Share link modal state (for subscription templates)
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareLink, setShareLink] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);
  // Create subscription template form state
  const [showCreateSubTemplate, setShowCreateSubTemplate] = useState(false);
  const [subTemplateName, setSubTemplateName] = useState('');
  const [subTemplateCycle, setSubTemplateCycle] = useState('month');
  const [subTemplateCycleNum, setSubTemplateCycleNum] = useState(1);
  const [subTemplateInstallments, setSubTemplateInstallments] = useState('0');
  const [subTemplateTaxRate, setSubTemplateTaxRate] = useState(0);
  const [subTemplateTaxLabel, setSubTemplateTaxLabel] = useState('');
  const [subTemplateFee, setSubTemplateFee] = useState(0);
  const [subTemplateFeeLabel, setSubTemplateFeeLabel] = useState('Fee');
  const [subTemplateItems, setSubTemplateItems] = useState<{
    description: string;
    quantity: number;
    price: number;
    isTaxable: boolean;
  }[]>([{
    description: '',
    quantity: 1,
    price: 0,
    isTaxable: true
  }]);
  const [subTemplateErrors, setSubTemplateErrors] = useState<Record<string, string>>({});
  const [isSavingSubTemplate, setIsSavingSubTemplate] = useState(false);
  // Edit subscription template state
  const [editingSubTemplateId, setEditingSubTemplateId] = useState<string | null>(null);
  const [isLoadingEditSubTemplate, setIsLoadingEditSubTemplate] = useState(false);
  const [editSubTemplateError, setEditSubTemplateError] = useState<string | null>(null);
  // Delete subscription template state
  const [deleteSubTemplateId, setDeleteSubTemplateId] = useState<string | null>(null);
  const [isDeletingSubTemplate, setIsDeletingSubTemplate] = useState(false);
  // Run subscription template state
  const [runTemplateId, setRunTemplateId] = useState<string | null>(null);
  const [runTemplateCustomerId, setRunTemplateCustomerId] = useState('');
  const [runTemplateStartDate, setRunTemplateStartDate] = useState('');
  const [isRunningTemplate, setIsRunningTemplate] = useState(false);
  const [runTemplateError, setRunTemplateError] = useState<string | null>(null);
  const [runTemplateSuccessNumber, setRunTemplateSuccessNumber] = useState<string | null>(null);
  // Payment methods for run template modal
  interface RunTemplatePaymentMethod {
    id: string;
    card_brand: string;
    card_exp: string;
    card_last_digits: string;
  }
  const [runTemplatePaymentMethods, setRunTemplatePaymentMethods] = useState<RunTemplatePaymentMethod[]>([]);
  const [runTemplateSelectedPaymentMethodId, setRunTemplateSelectedPaymentMethodId] = useState('');
  const [isLoadingRunTemplatePaymentMethods, setIsLoadingRunTemplatePaymentMethods] = useState(false);
  const SKIP_PAYMENT_ID = '__skip_payment__';
  // Terms form state
  const [termsName, setTermsName] = useState('');
  const [termsContent, setTermsContent] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [termsErrors, setTermsErrors] = useState<Record<string, string>>({});
  const companyTerms = getTermsByCompanyId(selectedCompanyId);
  // Template variables info
  const templateVariables = [{
    variable: '%invoice_link%',
    description: 'Link to view/pay invoice'
  }, {
    variable: '%invoice_number%',
    description: 'Invoice number'
  }, {
    variable: '%invoice_date%',
    description: 'Invoice date'
  }, {
    variable: '%invoice_status%',
    description: 'Invoice status'
  }, {
    variable: '%invoice_amount%',
    description: 'Invoice total amount'
  }, {
    variable: '%customer_name%',
    description: 'Customer name'
  }];
  // Initialize editor content when switching to templates tab or when editing company changes
  useEffect(() => {
    if (activeTab === 'templates' && emailEditorRef.current && !editorInitializedRef.current) {
      emailEditorRef.current.innerHTML = invoiceEmailTemplate;
      editorInitializedRef.current = true;
    }
  }, [activeTab, invoiceEmailTemplate]);
  // Reset editor initialized flag when switching tabs away from templates
  useEffect(() => {
    if (activeTab !== 'templates') {
      editorInitializedRef.current = false;
    }
  }, [activeTab]);
  // Sync editor content to state (call this before saving)
  const syncEditorContent = useCallback(() => {
    if (emailEditorRef.current) {
      setInvoiceEmailTemplate(emailEditorRef.current.innerHTML);
      return emailEditorRef.current.innerHTML;
    }
    return invoiceEmailTemplate;
  }, [invoiceEmailTemplate]);
  // Handle mouse move over editor to show link tooltips
  const handleEditorMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const link = target.closest('a');
    if (link) {
      const href = link.getAttribute('href');
      if (href) {
        const rect = link.getBoundingClientRect();
        setLinkTooltip({
          url: href,
          x: rect.left,
          y: rect.bottom + 4
        });
      }
    } else {
      setLinkTooltip(null);
    }
  }, []);
  const handleEditorMouseLeave = useCallback(() => {
    setLinkTooltip(null);
  }, []);
  // Handle paste event to strip HTML and insert plain text only
  const handleEditorPaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    // Get plain text from clipboard
    const text = e.clipboardData.getData('text/plain');
    // Insert plain text at cursor position
    if (text) {
      document.execCommand('insertText', false, text);
    }
  }, []);
  // Fetch subscription templates
  const fetchSubscriptionTemplates = async (companyId: string) => {
    setIsLoadingSubTemplates(true);
    setSubTemplatesError(null);
    try {
      const response = await callBillingAPI('getSubscriptionTemplates', {
        company_id: companyId
      });
      const templates: SubscriptionTemplate[] = Array.isArray(response) ? response : response.items || response.data || [];
      setSubscriptionTemplates(templates);
    } catch (error) {
      console.error('Failed to fetch subscription templates:', error);
      setSubTemplatesError(error instanceof Error ? error.message : 'Failed to load subscription templates');
    } finally {
      setIsLoadingSubTemplates(false);
    }
  };
  // Load subscription templates when switching to that tab
  useEffect(() => {
    if (activeTab === 'subscription_templates' && editingCompany) {
      fetchSubscriptionTemplates(editingCompany.id);
    }
  }, [activeTab, editingCompany]);
  // Share link handlers
  const handleShareTemplate = (template: SubscriptionTemplate) => {
    setShareLink(template.link || null);
    setShareModalOpen(true);
    setCopySuccess(false);
  };
  const handleCopyLink = async () => {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopySuccess(true);
      setTimeout(() => {
        setShareModalOpen(false);
        setCopySuccess(false);
      }, 1500);
    } catch (err) {
      console.error('Failed to copy:', err);
      alert('Please copy this link manually: ' + shareLink);
    }
  };
  const handleViewLink = () => {
    if (!shareLink) return;
    window.open(shareLink, '_blank');
    setShareModalOpen(false);
  };
  // Create subscription template handlers
  const openCreateSubTemplate = () => {
    setSubTemplateName('');
    setSubTemplateCycle('month');
    setSubTemplateCycleNum(1);
    setSubTemplateInstallments('0');
    setSubTemplateTaxRate(companyTaxRate || 0);
    setSubTemplateTaxLabel(companyTaxRate > 0 ? `Tax (${Math.round(companyTaxRate)}%)` : 'Tax');
    setSubTemplateFee(0);
    setSubTemplateFeeLabel('Fee');
    setSubTemplateItems([{
      description: '',
      quantity: 1,
      price: 0,
      isTaxable: true
    }]);
    setSubTemplateErrors({});
    setShowCreateSubTemplate(true);
  };
  const closeCreateSubTemplate = () => {
    setShowCreateSubTemplate(false);
    setSubTemplateErrors({});
  };
  const addSubTemplateItem = () => {
    setSubTemplateItems([...subTemplateItems, {
      description: '',
      quantity: 1,
      price: 0,
      isTaxable: true
    }]);
  };
  const removeSubTemplateItem = (index: number) => {
    setSubTemplateItems(subTemplateItems.filter((_, i) => i !== index));
  };
  const updateSubTemplateItem = (index: number, updates: Partial<{
    description: string;
    quantity: number;
    price: number;
    isTaxable: boolean;
  }>) => {
    setSubTemplateItems(subTemplateItems.map((item, i) => i === index ? {
      ...item,
      ...updates
    } : item));
  };
  const validateSubTemplateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!subTemplateName.trim()) {
      errors.name = 'Template name is required';
    }
    if (subTemplateItems.length === 0) {
      errors.items = 'Please add at least one item';
    }
    const hasEmptyTitle = subTemplateItems.some(item => !item.description.trim());
    if (hasEmptyTitle) {
      errors.items = 'Please fill in all item descriptions';
    }
    setSubTemplateErrors(errors);
    return Object.keys(errors).length === 0;
  };
  const handleSaveSubTemplate = async () => {
    if (!validateSubTemplateForm()) return;
    if (!editingCompany) return;
    setIsSavingSubTemplate(true);
    setSubTemplateErrors({});
    try {
      // Calculate subtotal
      const subtotal = subTemplateItems.reduce((sum, item) => sum + item.quantity * item.price, 0);
      // Calculate tax on taxable items using company tax rate
      const taxableSubtotal = subTemplateItems.filter(item => item.isTaxable).reduce((sum, item) => sum + item.quantity * item.price, 0);
      const taxAmount = taxableSubtotal * (companyTaxRate / 100);
      // Grand total
      const grandTotal = subtotal + taxAmount + subTemplateFee;
      const items = subTemplateItems.map(item => ({
        title: item.description,
        qty: item.quantity,
        price: item.price,
        amount: item.quantity * item.price,
        taxable: item.isTaxable ? 1 : 0
      }));
      const apiParams: Record<string, any> = {
        company_id: editingCompany.id,
        name: subTemplateName.trim(),
        items,
        amount: grandTotal,
        tax: {
          amount: taxAmount,
          label: subTemplateTaxLabel || 'Tax'
        },
        fee: {
          amount: subTemplateFee,
          label: subTemplateFeeLabel || 'Fee'
        },
        cycle: subTemplateCycle,
        cycle_num: subTemplateCycleNum,
        installments: parseInt(subTemplateInstallments)
      };
      console.log('Creating subscription template:', apiParams);
      await callBillingAPI('createSubscriptionTemplate', apiParams);
      // Return to list and refresh
      setShowCreateSubTemplate(false);
      await fetchSubscriptionTemplates(editingCompany.id);
    } catch (error) {
      console.error('Failed to create subscription template:', error);
      setSubTemplateErrors({
        submit: error instanceof Error ? error.message : 'Failed to create subscription template'
      });
    } finally {
      setIsSavingSubTemplate(false);
    }
  };
  // Edit subscription template handlers
  const openEditSubTemplate = async (templateId: string) => {
    setEditingSubTemplateId(templateId);
    setIsLoadingEditSubTemplate(true);
    setEditSubTemplateError(null);
    setShowCreateSubTemplate(false);
    setSubTemplateErrors({});
    try {
      const response = await callBillingAPI('getSubscriptionTemplate', {
        id: templateId
      });
      console.log('Subscription template details:', response);
      // Populate form fields from response
      setSubTemplateName(response.name || '');
      setSubTemplateCycle(response.cycle || 'month');
      setSubTemplateCycleNum(parseInt(String(response.cycle_num)) || 1);
      setSubTemplateInstallments(String(parseInt(String(response.installments)) || 0));
      setSubTemplateTaxLabel(response.tax_label || (companyTaxRate > 0 ? `Tax (${Math.round(companyTaxRate)}%)` : 'Tax'));
      setSubTemplateFee(parseFloat(String(response.fee)) || 0);
      setSubTemplateFeeLabel(response.fee_label || 'Fee');
      // Transform items
      if (response.items && Array.isArray(response.items)) {
        const transformedItems = response.items.map((item: any) => ({
          description: item.title || '',
          quantity: parseFloat(String(item.qty)) || 1,
          price: parseFloat(String(item.price)) || 0,
          isTaxable: item.taxable === 1 || item.taxable === true
        }));
        setSubTemplateItems(transformedItems.length > 0 ? transformedItems : [{
          description: '',
          quantity: 1,
          price: 0,
          isTaxable: true
        }]);
      } else {
        setSubTemplateItems([{
          description: '',
          quantity: 1,
          price: 0,
          isTaxable: true
        }]);
      }
    } catch (error) {
      console.error('Failed to fetch subscription template:', error);
      setEditSubTemplateError(error instanceof Error ? error.message : 'Failed to load template details');
    } finally {
      setIsLoadingEditSubTemplate(false);
    }
  };
  const closeEditSubTemplate = () => {
    setEditingSubTemplateId(null);
    setEditSubTemplateError(null);
    setSubTemplateErrors({});
  };
  const handleUpdateSubTemplate = async () => {
    if (!validateSubTemplateForm()) return;
    if (!editingCompany || !editingSubTemplateId) return;
    setIsSavingSubTemplate(true);
    setSubTemplateErrors({});
    try {
      const subtotal = subTemplateItems.reduce((sum, item) => sum + item.quantity * item.price, 0);
      const taxableSubtotal = subTemplateItems.filter(item => item.isTaxable).reduce((sum, item) => sum + item.quantity * item.price, 0);
      const taxAmount = taxableSubtotal * (companyTaxRate / 100);
      const grandTotal = subtotal + taxAmount + subTemplateFee;
      const items = subTemplateItems.map(item => ({
        title: item.description,
        qty: item.quantity,
        price: item.price,
        amount: item.quantity * item.price,
        taxable: item.isTaxable ? 1 : 0
      }));
      const apiParams: Record<string, any> = {
        id: editingSubTemplateId,
        name: subTemplateName.trim(),
        items,
        amount: grandTotal,
        tax: {
          amount: taxAmount,
          label: subTemplateTaxLabel || 'Tax'
        },
        fee: {
          amount: subTemplateFee,
          label: subTemplateFeeLabel || 'Fee'
        },
        cycle: subTemplateCycle,
        cycle_num: subTemplateCycleNum,
        installments: parseInt(subTemplateInstallments)
      };
      console.log('Updating subscription template:', apiParams);
      await callBillingAPI('editSubscriptionTemplate', apiParams);
      // Return to list and refresh
      setEditingSubTemplateId(null);
      await fetchSubscriptionTemplates(editingCompany.id);
    } catch (error) {
      console.error('Failed to update subscription template:', error);
      setSubTemplateErrors({
        submit: error instanceof Error ? error.message : 'Failed to update subscription template'
      });
    } finally {
      setIsSavingSubTemplate(false);
    }
  };
  // Delete subscription template handler
  const handleDeleteSubTemplate = async () => {
    if (!deleteSubTemplateId || !editingCompany) return;
    setIsDeletingSubTemplate(true);
    try {
      await callBillingAPI('deleteSubscriptionTemplate', {
        id: deleteSubTemplateId
      });
      setDeleteSubTemplateId(null);
      await fetchSubscriptionTemplates(editingCompany.id);
    } catch (error) {
      console.error('Failed to delete subscription template:', error);
      alert(error instanceof Error ? error.message : 'Failed to delete template');
      setDeleteSubTemplateId(null);
    } finally {
      setIsDeletingSubTemplate(false);
    }
  };
  // Run subscription template handlers
  const getRunTemplateCardBrandDisplay = (brand: string) => {
    const brandUpper = brand?.toUpperCase() || 'CARD';
    const brandColors: Record<string, string> = {
      VISA: 'bg-blue-600',
      MASTERCARD: 'bg-red-500',
      AMEX: 'bg-blue-400',
      DISCOVER: 'bg-orange-500'
    };
    return {
      name: brandUpper,
      color: brandColors[brandUpper] || 'bg-gray-600'
    };
  };
  const isRunTemplateStartDateFuture = (() => {
    if (!runTemplateStartDate) return false;
    const start = new Date(runTemplateStartDate);
    start.setHours(0, 0, 0, 0);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return start.getTime() > now.getTime();
  })();
  // When start date becomes future, clear the "skip payment" selection
  useEffect(() => {
    if (isRunTemplateStartDateFuture && runTemplateSelectedPaymentMethodId === SKIP_PAYMENT_ID) {
      if (runTemplatePaymentMethods.length > 0) {
        setRunTemplateSelectedPaymentMethodId(runTemplatePaymentMethods[0].id);
      } else {
        setRunTemplateSelectedPaymentMethodId('');
      }
    }
  }, [isRunTemplateStartDateFuture, runTemplateSelectedPaymentMethodId, runTemplatePaymentMethods]);
  const openRunTemplateModal = async (templateId: string) => {
    setRunTemplateId(templateId);
    setRunTemplateCustomerId('');
    setRunTemplateError(null);
    setRunTemplatePaymentMethods([]);
    setRunTemplateSelectedPaymentMethodId('');
    setIsLoadingRunTemplatePaymentMethods(false);
    // Set default start date to today (yyyy-mm-dd for input[type=date])
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    setRunTemplateStartDate(`${yyyy}-${mm}-${dd}`);
  };
  // Fetch payment methods when customer is selected in run template modal
  useEffect(() => {
    const fetchRunTemplatePaymentMethods = async () => {
      if (!runTemplateCustomerId || !runTemplateId) {
        setRunTemplatePaymentMethods([]);
        setRunTemplateSelectedPaymentMethodId('');
        return;
      }
      setIsLoadingRunTemplatePaymentMethods(true);
      try {
        const response = await callBillingAPI('getCustomerPaymentMethods', {
          customer_id: runTemplateCustomerId
        });
        let methods: RunTemplatePaymentMethod[] = [];
        if (Array.isArray(response)) {
          methods = response;
        } else if (response.items && Array.isArray(response.items)) {
          methods = response.items;
        } else if (response.data && Array.isArray(response.data)) {
          methods = response.data;
        }
        setRunTemplatePaymentMethods(methods);
        // Auto-select first payment method
        if (methods.length > 0) {
          setRunTemplateSelectedPaymentMethodId(methods[0].id);
        } else {
          setRunTemplateSelectedPaymentMethodId('');
        }
      } catch (error) {
        console.error('Failed to fetch payment methods:', error);
        setRunTemplatePaymentMethods([]);
        setRunTemplateSelectedPaymentMethodId('');
      } finally {
        setIsLoadingRunTemplatePaymentMethods(false);
      }
    };
    fetchRunTemplatePaymentMethods();
  }, [runTemplateCustomerId, runTemplateId]);
  const handleRunTemplate = async () => {
    if (!runTemplateId || !runTemplateCustomerId) {
      setRunTemplateError('Please select a customer');
      return;
    }
    if (!runTemplateStartDate) {
      setRunTemplateError('Please select a start date');
      return;
    }
    setIsRunningTemplate(true);
    setRunTemplateError(null);
    try {
      // Convert yyyy-mm-dd to mm-dd-yyyy for API
      const [year, month, day] = runTemplateStartDate.split('-');
      const formattedDate = `${month}-${day}-${year}`;
      const apiParams: Record<string, any> = {
        id: runTemplateId,
        customer_id: runTemplateCustomerId,
        start_date: formattedDate
      };
      // Handle payment method and ignore_auto_pay logic
      if (runTemplatePaymentMethods.length === 0) {
        // No payment methods → ignore auto pay
        apiParams.ignore_auto_pay = 1;
      } else if (runTemplateSelectedPaymentMethodId === SKIP_PAYMENT_ID) {
        // User chose "Don't pay the first invoice right now"
        apiParams.ignore_auto_pay = 1;
      } else if (runTemplateSelectedPaymentMethodId) {
        // Normal payment method selected
        apiParams.customer_payment_method_id = runTemplateSelectedPaymentMethodId;
      }
      const newSubscr = await callBillingAPI('runSubscriptionTemplate', apiParams);
      console.log('New subscription created:', newSubscr);
      setRunTemplateId(null);
      setRunTemplateSuccessNumber(newSubscr.number || newSubscr.id || 'N/A');
    } catch (error) {
      console.error('Failed to run subscription template:', error);
      setRunTemplateError(error instanceof Error ? error.message : 'Failed to create subscription');
    } finally {
      setIsRunningTemplate(false);
    }
  };
  // Format billing cycle for subscription templates
  const formatBillingCycle = (cycle: string, cycleNum: number): string => {
    const cycleNames: Record<string, {
      singular: string;
      plural: string;
    }> = {
      week: {
        singular: 'Week',
        plural: 'Weeks'
      },
      month: {
        singular: 'Month',
        plural: 'Months'
      },
      year: {
        singular: 'Year',
        plural: 'Years'
      }
    };
    const cycleName = cycleNames[cycle] || {
      singular: cycle,
      plural: cycle
    };
    if (cycleNum === 1) {
      return `Every ${cycleName.singular}`;
    }
    return `Every ${cycleNum} ${cycleName.plural}`;
  };
  // Fetch companies from API
  const fetchCompanies = async () => {
    setIsLoadingCompanies(true);
    setCompaniesError(null);
    try {
      const response = await callBillingAPI('getCompanies', {});
      console.log('API Response:', response);
      // Handle different possible response structures
      let apiCompanies: ApiCompany[] = [];
      if (Array.isArray(response)) {
        apiCompanies = response;
      } else if (response.companies && Array.isArray(response.companies)) {
        apiCompanies = response.companies;
      } else if (response.data && Array.isArray(response.data)) {
        apiCompanies = response.data;
      } else {
        console.error('Unexpected API response structure:', response);
        throw new Error('Invalid API response format');
      }
      console.log('Parsed companies:', apiCompanies);
      // Store full company data
      const transformedCompanies: CompanyData[] = apiCompanies.map(apiCompany => ({
        id: apiCompany.id,
        name: apiCompany.name,
        email: apiCompany.email || '',
        phone: apiCompany.phone || '',
        alias: apiCompany.alias,
        stripe_public_key: apiCompany.stripe_public_key || '',
        stripe_private_key: apiCompany.stripe_private_key || '',
        logo: apiCompany.logo || '',
        tax_rate: apiCompany.tax_rate ?? 0,
        invoice_email_template: apiCompany.invoice_email_template || '',
        invoice_email_subject_template: apiCompany.invoice_email_subject_template || '',
        invoice_sms_template: apiCompany.invoice_sms_template || ''
      }));
      console.log('Transformed companies:', transformedCompanies);
      setCompanies(transformedCompanies);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
      setCompaniesError(error instanceof Error ? error.message : 'Failed to load companies');
    } finally {
      setIsLoadingCompanies(false);
    }
  };
  useEffect(() => {
    fetchCompanies();
  }, []);
  // Listen for custom event to reset to company list view (when clicking Settings nav while on Settings page)
  useEffect(() => {
    const handleResetToList = () => {
      setEditingCompany(null);
      setActiveTab('general');
      editorInitializedRef.current = false;
    };
    window.addEventListener(SETTINGS_RESET_EVENT, handleResetToList);
    return () => {
      window.removeEventListener(SETTINGS_RESET_EVENT, handleResetToList);
    };
  }, []);
  // Listen for navigation state to reset to company list view (when navigating from another page)
  useEffect(() => {
    const state = location.state as {
      showCompanyList?: boolean;
      openSubscriptionTemplates?: boolean;
    } | null;
    if (state?.showCompanyList) {
      setEditingCompany(null);
      setActiveTab('general');
      editorInitializedRef.current = false;
      // Clear the state to prevent re-triggering on subsequent renders
      window.history.replaceState({}, document.title);
    }
    if (state?.openSubscriptionTemplates) {
      // Find the company matching selectedCompanyId and open it with subscription_templates tab
      const matchingCompany = companies.find(c => c.id === selectedCompanyId);
      if (matchingCompany) {
        openEditCompany(matchingCompany);
        setActiveTab('subscription_templates');
        // Clear the state to prevent re-triggering
        window.history.replaceState({}, document.title);
      }
      // If companies haven't loaded yet, the effect below will handle it
    }
  }, [location.state, companies, selectedCompanyId]);
  const openEditCompany = (company: CompanyData) => {
    setEditingCompany(company);
    setActiveTab('general');
    setCompanyName(company.name || '');
    setCompanyEmail(company.email || '');
    setCompanyPhone(company.phone || '');
    setCompanyStripePublicKey(company.stripe_public_key || '');
    setCompanyStripePrivateKey(''); // Always start with empty private key since API doesn't return it
    setCompanyTaxRate(company.tax_rate ?? 0);
    setEnterNewPrivateKey(false); // Reset checkbox
    setCompanyLogo(company.logo || null);
    setLogoChanged(false); // Reset logo changed flag
    setInvoiceEmailTemplate(company.invoice_email_template || '');
    setInvoiceEmailSubjectTemplate(company.invoice_email_subject_template || '');
    setInvoiceSmsTemplate(company.invoice_sms_template || '');
    editorInitializedRef.current = false; // Reset editor initialization
    setCompanyErrors({});
  };
  const closeEditCompany = () => {
    setEditingCompany(null);
    setActiveTab('general');
    editorInitializedRef.current = false;
    setCompanyErrors({});
  };
  const validateCompanyForm = (): boolean => {
    const errors: Record<string, string> = {};
    // Company name is required
    if (!companyName || !companyName.trim()) {
      errors.name = 'Company name is required';
    }
    // Email is optional, but if provided, must be valid
    if (companyEmail && companyEmail.trim()) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(companyEmail.trim())) {
        errors.email = 'Invalid email format';
      }
    }
    // Tax rate must be between 0 and 100
    if (companyTaxRate < 0 || companyTaxRate > 100) {
      errors.tax_rate = 'Tax rate must be between 0 and 100';
    }
    // Stripe keys are optional - no validation needed
    setCompanyErrors(errors);
    return Object.keys(errors).length === 0;
  };
  // Handle logo file upload
  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setCompanyErrors({
        ...companyErrors,
        logo: 'Please select an image file'
      });
      return;
    }
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setCompanyErrors({
        ...companyErrors,
        logo: 'Image must be less than 2MB'
      });
      return;
    }
    // Read file as base64
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      // Remove the data URL prefix to get just the base64 data
      const base64Data = base64.split(',')[1];
      setCompanyLogo(base64Data);
      setLogoChanged(true);
      setCompanyErrors({
        ...companyErrors,
        logo: undefined
      } as Record<string, string>);
    };
    reader.onerror = () => {
      setCompanyErrors({
        ...companyErrors,
        logo: 'Failed to read image file'
      });
    };
    reader.readAsDataURL(file);
  };
  // Remove logo
  const handleRemoveLogo = () => {
    setCompanyLogo(null);
    setLogoChanged(true);
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
    }
  };
  // Rich text editor commands - don't update state, just execute the command
  const execCommand = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    emailEditorRef.current?.focus();
  };
  // Insert variable into editor at cursor position
  const insertVariable = (variable: string) => {
    if (emailEditorRef.current) {
      emailEditorRef.current.focus();
      document.execCommand('insertText', false, variable);
    }
  };
  // Insert variable into SMS textarea
  const insertSmsVariable = (variable: string) => {
    setInvoiceSmsTemplate(prev => prev + variable);
  };
  const handleSaveCompany = async () => {
    console.log('handleSaveCompany called');
    console.log('Editing company:', editingCompany);
    if (!validateCompanyForm()) {
      console.log('Validation failed:', companyErrors);
      setActiveTab('general'); // Switch to general tab to show errors
      return;
    }
    if (!editingCompany) {
      console.log('No editing company found');
      return;
    }
    setIsSavingCompany(true);
    setCompanyErrors({}); // Clear previous errors
    // Sync editor content before saving
    const currentEmailTemplate = syncEditorContent();
    // Determine if we should send the private key
    // Send it if: public key is empty (new setup) OR user checked "enter new private key"
    const hasExistingPublicKey = !!editingCompany.stripe_public_key;
    const shouldSendPrivateKey = !hasExistingPublicKey || enterNewPrivateKey;
    try {
      const apiParams: Record<string, string | number> = {
        id: editingCompany.id,
        name: companyName,
        email: companyEmail || '',
        phone: companyPhone || '',
        stripe_public_key: companyStripePublicKey || '',
        tax_rate: Math.round(companyTaxRate),
        invoice_email_template: currentEmailTemplate || '',
        invoice_email_subject_template: invoiceEmailSubjectTemplate || '',
        invoice_sms_template: invoiceSmsTemplate || ''
      };
      // Only include private key if appropriate
      if (shouldSendPrivateKey) {
        apiParams.stripe_private_key = companyStripePrivateKey || '';
      }
      // Only include logo if it was changed
      if (logoChanged) {
        apiParams.logo = companyLogo || '';
      }
      console.log('Calling editCompany API with:', apiParams);
      const response = await callBillingAPI('editCompany', apiParams);
      console.log('Edit company response:', response);
      // Refresh companies list (both local and context)
      await fetchCompanies();
      await refreshCompanies();
      setEditingCompany(null);
      editorInitializedRef.current = false;
    } catch (error) {
      console.error('Failed to save company:', error);
      setCompanyErrors({
        submit: error instanceof Error ? error.message : 'Failed to save company'
      });
    } finally {
      setIsSavingCompany(false);
    }
  };
  const openCreateTermsModal = () => {
    setEditingTerms(null);
    setTermsName('');
    setTermsContent('');
    setIsDefault(companyTerms.length === 0);
    setTermsErrors({});
    setIsTermsModalOpen(true);
  };
  const openEditTermsModal = (terms: TermsAndConditions) => {
    setEditingTerms(terms);
    setTermsName(terms.name);
    setTermsContent(terms.content);
    setIsDefault(terms.is_default);
    setTermsErrors({});
    setIsTermsModalOpen(true);
  };
  const validateTermsForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!termsName.trim()) {
      errors.name = 'Name is required';
    }
    if (!termsContent.trim()) {
      errors.content = 'Content is required';
    }
    setTermsErrors(errors);
    return Object.keys(errors).length === 0;
  };
  const handleSaveTerms = async () => {
    if (!validateTermsForm()) return;
    setIsLoading(true);
    try {
      if (editingTerms) {
        await updateTerms(editingTerms.id, {
          name: termsName,
          content: termsContent,
          is_default: isDefault
        });
      } else {
        await createTerms({
          company_id: selectedCompanyId,
          name: termsName,
          content: termsContent,
          is_default: isDefault
        });
      }
      setIsTermsModalOpen(false);
    } catch (error) {
      console.error('Failed to save terms:', error);
    } finally {
      setIsLoading(false);
    }
  };
  const handleDeleteTerms = async (id: string) => {
    setIsLoading(true);
    try {
      await deleteTerms(id);
      setDeleteConfirmId(null);
    } catch (error) {
      console.error('Failed to delete terms:', error);
    } finally {
      setIsLoading(false);
    }
  };
  const companyColumns = [{
    key: 'name',
    header: 'Company Name',
    render: (company: CompanyData) => <div className="flex items-center gap-3" data-id="element-1958">
          {company.logo ? <img src={`data:image/png;base64,${company.logo}`} alt={company.name} className="w-20 h-12 rounded-lg object-contain bg-gray-50 p-1" data-id="element-1959" /> : <div className="w-20 h-12 bg-blue-100 rounded-lg flex items-center justify-center" data-id="element-1960">
              <BuildingIcon className="w-6 h-6 text-blue-600" data-id="element-1961" />
            </div>}
          <span className="font-medium text-gray-900" data-id="element-1962">{company.name}</span>
        </div>
  }, {
    key: 'tax_rate',
    header: 'Tax Rate',
    render: (company: CompanyData) => <div className="flex items-center gap-1" data-id="element-1963">
          <PercentIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-1964" />
          <span className="text-sm text-gray-700" data-id="element-1965">
            {company.tax_rate ?? 0}%
          </span>
        </div>
  }, {
    key: 'stripe_key',
    header: 'Stripe',
    render: (company: CompanyData) => company.stripe_public_key ? <span className="inline-flex items-center text-sm font-medium text-green-700" data-id="element-1966">
            Set
          </span> : <span className="inline-flex items-center text-sm font-medium text-red-600" data-id="element-1967">
            Not Set
          </span>
  }, {
    key: 'actions',
    header: '',
    render: (company: CompanyData) => <div className="flex items-center justify-end" data-id="element-1968">
          <Button variant="ghost" size="sm" onClick={() => openEditCompany(company)} aria-label="Edit company" data-id="element-1969">
            <EditIcon className="w-4 h-4" data-id="element-1970" />
          </Button>
        </div>
  }];
  const subscriptionTemplateColumns = [{
    key: 'name',
    header: 'Name',
    render: (template: SubscriptionTemplate) => <span className="font-medium text-gray-900" data-id="element-1971">
          {template.name || '—'}
        </span>
  }, {
    key: 'cycle',
    header: 'Billing Cycle',
    render: (template: SubscriptionTemplate) => <span className="text-gray-700" data-id="element-1972">
          {formatBillingCycle(template.cycle, parseInt(String(template.cycle_num)) || 1)}
        </span>
  }, {
    key: 'amount',
    header: 'Amount',
    render: (template: SubscriptionTemplate) => <span className="font-medium text-gray-900" data-id="element-1973">
          {formatCurrency(parseFloat(String(template.amount)) || 0)}
        </span>
  }, {
    key: 'actions',
    header: '',
    render: (template: SubscriptionTemplate) => <div className="flex items-center justify-end gap-1" data-id="element-1974">
          <Button variant="ghost" size="sm" onClick={() => openRunTemplateModal(template.id)} className="text-gray-600 hover:text-green-600 hover:bg-green-50" aria-label="Run template" title="Create subscription from template" data-id="element-1975">
            <PlayIcon className="w-4 h-4" data-id="element-1976" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => handleShareTemplate(template)} className="text-gray-600 hover:text-blue-600 hover:bg-blue-50" aria-label="Share link" title="Share link" disabled={!template.link} data-id="element-1977">
            <Share2Icon className="w-4 h-4" data-id="element-1978" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => openEditSubTemplate(template.id)} aria-label="Edit template" title="Edit template" data-id="element-1979">
            <EditIcon className="w-4 h-4" data-id="element-1980" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setDeleteSubTemplateId(template.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50" aria-label="Delete template" title="Delete template" data-id="element-1981">
            <TrashIcon className="w-4 h-4" data-id="element-1982" />
          </Button>
        </div>
  }];
  const termsColumns = [{
    key: 'name',
    header: 'Name',
    render: (terms: TermsAndConditions) => <div className="flex items-center gap-2" data-id="element-1983">
          <FileTextIcon className="w-4 h-4 text-gray-400" data-id="element-1984" />
          <span className="font-medium text-gray-900" data-id="element-1985">{terms.name}</span>
          {terms.is_default && <Badge variant="info" className="ml-2" data-id="element-1986">
              Default
            </Badge>}
        </div>
  }, {
    key: 'updated',
    header: 'Last Updated',
    render: (terms: TermsAndConditions) => <span className="text-sm text-gray-500" data-id="element-1987">
          {formatDate(terms.updated_at)}
        </span>
  }, {
    key: 'actions',
    header: '',
    render: (terms: TermsAndConditions) => <div className="flex items-center justify-end gap-2" data-id="element-1988">
          <Button variant="ghost" size="sm" onClick={() => openEditTermsModal(terms)} aria-label="Edit terms" data-id="element-1989">
            <EditIcon className="w-4 h-4" data-id="element-1990" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setDeleteConfirmId(terms.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50" aria-label="Delete terms" disabled={terms.is_default} data-id="element-1991">
            <TrashIcon className="w-4 h-4" data-id="element-1992" />
          </Button>
        </div>
  }];
  // Tab component for Edit Company page
  const TabButton = ({
    tab,
    label,
    icon
  }: {
    tab: EditCompanyTab;
    label: string;
    icon: React.ReactNode;
  }) => <button type="button" onClick={() => {
    // Sync editor content before switching tabs
    if (activeTab === 'templates') {
      syncEditorContent();
    }
    setActiveTab(tab);
  }} className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === tab ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-1993">
      {icon}
      {label}
    </button>;
  // If editing a company, show the edit form instead of the settings list
  if (editingCompany) {
    return <div className="space-y-6" data-id="element-1994">
        {/* Page Header with Back Button */}
        <div className="flex items-center gap-4" data-id="element-1995">
          <Button variant="ghost" size="sm" onClick={closeEditCompany} leftIcon={<ArrowLeftIcon className="w-4 h-4" data-id="element-1997" />} data-id="element-1996">
            Back to Settings
          </Button>
        </div>

        <div data-id="element-1998">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1999">Edit Company</h1>
          <p className="text-gray-500 mt-1" data-id="element-2000">
            Update company information and settings
          </p>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200" data-id="element-2001">
          <div className="flex gap-4" data-id="element-2002">
            <TabButton tab="general" label="General Settings" icon={<SettingsIcon className="w-4 h-4" data-id="element-2004" />} data-id="element-2003" />
            <TabButton tab="templates" label="Email/SMS Templates" icon={<MailIcon className="w-4 h-4" data-id="element-2006" />} data-id="element-2005" />
            <TabButton tab="subscription_templates" label="Subscription Templates" icon={<RepeatIcon className="w-4 h-4" data-id="element-2008" />} data-id="element-2007" />
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'general' && <Card data-id="element-2009">
            <div className="space-y-6" data-id="element-2010">
              {/* Logo Upload */}
              <div data-id="element-2011">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2012">
                  Company Logo
                </label>
                <div className="flex items-center gap-4" data-id="element-2013">
                  {companyLogo ? <div className="relative" data-id="element-2014">
                      <img src={`data:image/png;base64,${companyLogo}`} alt="Company logo" className="w-20 h-20 rounded-lg object-cover border border-gray-200" data-id="element-2015" />
                      <button type="button" onClick={handleRemoveLogo} className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors" aria-label="Remove logo" data-id="element-2016">
                        <XIcon className="w-4 h-4" data-id="element-2017" />
                      </button>
                    </div> : <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center border border-gray-200" data-id="element-2018">
                      <BuildingIcon className="w-10 h-10 text-gray-400" data-id="element-2019" />
                    </div>}
                  <div data-id="element-2020">
                    <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" id="logo-upload" data-id="element-2021" />
                    <Button variant="secondary" size="sm" onClick={() => logoInputRef.current?.click()} leftIcon={<UploadIcon className="w-4 h-4" data-id="element-2023" />} data-id="element-2022">
                      {companyLogo ? 'Change Logo' : 'Upload Logo'}
                    </Button>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-2024">
                      PNG, JPG up to 2MB
                    </p>
                  </div>
                </div>
                {companyErrors.logo && <p className="text-sm text-red-600 mt-1" data-id="element-2025">
                    {companyErrors.logo}
                  </p>}
              </div>

              {/* Form Fields in Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2026">
                <Input label="Company Name" value={companyName} onChange={e => setCompanyName(e.target.value)} error={companyErrors.name} placeholder="e.g., Acme Corporation" data-id="element-2027" />

                <Input label="Email" type="email" value={companyEmail} onChange={e => setCompanyEmail(e.target.value)} error={companyErrors.email} placeholder="billing@company.com" data-id="element-2028" />

                <Input label="Phone" type="tel" value={companyPhone} onChange={e => setCompanyPhone(e.target.value)} placeholder="(555) 123-4567" data-id="element-2029" />

                <Input label="Tax Rate (%)" type="number" min="0" max="100" step="1" value={companyTaxRate} onChange={e => setCompanyTaxRate(parseFloat(e.target.value) || 0)} error={companyErrors.tax_rate} placeholder="e.g., 6" helperText="Enter tax rate as a percentage (0-100)" data-id="element-2030" />
              </div>

              {/* Stripe Settings Section */}
              <div className="border-t border-gray-200 pt-6" data-id="element-2031">
                <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-2032">
                  Stripe Integration
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2033">
                  <Input label="Stripe Public Key" value={companyStripePublicKey} onChange={e => setCompanyStripePublicKey(e.target.value)} error={companyErrors.stripe_public_key} placeholder="pk_live_..." data-id="element-2034" />

                  {/* Show private key input based on whether public key exists */}
                  {!editingCompany?.stripe_public_key ?
              // No existing public key - show private key input directly
              <Input label="Stripe Private Key" type="password" value={companyStripePrivateKey} onChange={e => setCompanyStripePrivateKey(e.target.value)} error={companyErrors.stripe_private_key} placeholder="sk_live_..." helperText="Your private key is encrypted and stored securely" data-id="element-2035" /> :
              // Existing public key - show checkbox to optionally enter new private key
              <div className="space-y-3" data-id="element-2036">
                      <Checkbox label="Enter new Stripe private key" checked={enterNewPrivateKey} onChange={e => {
                  setEnterNewPrivateKey(e.target.checked);
                  if (!e.target.checked) {
                    setCompanyStripePrivateKey(''); // Clear private key when unchecking
                  }
                }} data-id="element-2037" />
                      {enterNewPrivateKey && <Input label="Stripe Private Key" type="password" value={companyStripePrivateKey} onChange={e => setCompanyStripePrivateKey(e.target.value)} error={companyErrors.stripe_private_key} placeholder="sk_live_..." helperText="Your private key is encrypted and stored securely" data-id="element-2038" />}
                    </div>}
                </div>
              </div>

              {companyErrors.submit && <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-2039">
                  <p className="text-sm text-red-600" data-id="element-2040">{companyErrors.submit}</p>
                </div>}

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200" data-id="element-2041">
                <Button variant="secondary" onClick={closeEditCompany} disabled={isSavingCompany} data-id="element-2042">
                  Cancel
                </Button>
                <Button onClick={handleSaveCompany} isLoading={isSavingCompany} data-id="element-2043">
                  Save Changes
                </Button>
              </div>
            </div>
          </Card>}

        {activeTab === 'templates' && <div className="space-y-6" data-id="element-2044">
            {/* Available Variables */}
            <Card data-id="element-2045">
              <div className="flex items-start gap-3" data-id="element-2046">
                <div className="p-2 bg-blue-50 rounded-lg" data-id="element-2047">
                  <FileTextIcon className="w-5 h-5 text-blue-600" data-id="element-2048" />
                </div>
                <div data-id="element-2049">
                  <h3 className="text-sm font-medium text-gray-900 mb-2" data-id="element-2050">
                    Available Variables
                  </h3>
                  <p className="text-sm text-gray-500 mb-3" data-id="element-2051">
                    Use these variables in your templates. They will be replaced
                    with actual values when sending.
                  </p>
                  <div className="flex flex-wrap gap-2" data-id="element-2052">
                    {templateVariables.map(v => <code key={v.variable} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded font-mono" title={v.description} data-id="element-2053">
                        {v.variable}
                      </code>)}
                  </div>
                </div>
              </div>
            </Card>

            {/* Email Template */}
            <Card data-id="element-2054">
              <div className="space-y-4" data-id="element-2055">
                <div className="flex items-center gap-2" data-id="element-2056">
                  <MailIcon className="w-5 h-5 text-gray-400" data-id="element-2057" />
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-2058">
                    Invoice Email Template
                  </h3>
                </div>
                <p className="text-sm text-gray-500" data-id="element-2059">
                  This template will be used when sending invoices via email.
                </p>

                {/* Email Subject */}
                <Input label="Email Subject" value={invoiceEmailSubjectTemplate} onChange={e => setInvoiceEmailSubjectTemplate(e.target.value)} placeholder="e.g., Invoice #%invoice_number% from Your Company" helperText="You can use template variables in the subject line" data-id="element-2060" />

                {/* Minimal Rich Text Toolbar */}
                <div data-id="element-2061">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-2062">
                    Email Body
                  </label>
                  <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-2063">
                    <div className="flex items-center gap-1 p-2 bg-gray-50 border-b border-gray-200 flex-wrap" data-id="element-2064">
                      <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => execCommand('bold')} className="p-1.5 rounded hover:bg-gray-200 transition-colors" title="Bold" data-id="element-2065">
                        <span className="font-bold text-sm" data-id="element-2066">B</span>
                      </button>
                      <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => execCommand('italic')} className="p-1.5 rounded hover:bg-gray-200 transition-colors" title="Italic" data-id="element-2067">
                        <span className="italic text-sm" data-id="element-2068">I</span>
                      </button>
                      <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => execCommand('underline')} className="p-1.5 rounded hover:bg-gray-200 transition-colors" title="Underline" data-id="element-2069">
                        <span className="underline text-sm" data-id="element-2070">U</span>
                      </button>
                      <div className="w-px h-5 bg-gray-300 mx-1" data-id="element-2071" />
                      {/* Font Size Dropdown */}
                      <div className="relative group" data-id="element-2072">
                        <button type="button" className="px-2 py-1 rounded hover:bg-gray-200 transition-colors text-xs text-gray-600 flex items-center gap-1" title="Font Size" data-id="element-2073">
                          <span className="text-sm" data-id="element-2074">A</span>
                          <span className="text-[10px]" data-id="element-2075">▾</span>
                        </button>
                        <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10 min-w-[100px]" data-id="element-2076">
                          {[{
                        value: '1',
                        label: 'Small'
                      }, {
                        value: '2',
                        label: 'Normal'
                      }, {
                        value: '3',
                        label: 'Medium'
                      }, {
                        value: '4',
                        label: 'Large'
                      }, {
                        value: '5',
                        label: 'X-Large'
                      }, {
                        value: '6',
                        label: 'XX-Large'
                      }].map(size => <button key={size.value} type="button" onMouseDown={e => e.preventDefault()} onClick={() => execCommand('fontSize', size.value)} className="w-full px-3 py-1.5 text-left text-sm hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg" data-id="element-2077">
                              {size.label}
                            </button>)}
                        </div>
                      </div>
                      <div className="w-px h-5 bg-gray-300 mx-1" data-id="element-2078" />
                      <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => {
                    const url = prompt('Enter link URL:');
                    if (url) execCommand('createLink', url);
                  }} className="p-1.5 rounded hover:bg-gray-200 transition-colors text-sm" title="Insert Link" data-id="element-2079">
                        🔗
                      </button>
                      <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => execCommand('unlink')} className="p-1.5 rounded hover:bg-gray-200 transition-colors text-sm text-red-500" title="Remove Link" data-id="element-2080">
                        🔗✕
                      </button>
                      <div className="w-px h-5 bg-gray-300 mx-1" data-id="element-2081" />
                      <div className="relative group" data-id="element-2082">
                        <button type="button" className="px-2 py-1 rounded hover:bg-gray-200 transition-colors text-xs text-gray-600" data-id="element-2083">
                          Insert Variable ▾
                        </button>
                        <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10 min-w-[180px] max-h-[200px] overflow-y-auto" data-id="element-2084">
                          {templateVariables.map(v => <button key={v.variable} type="button" onMouseDown={e => e.preventDefault()} onClick={() => insertVariable(v.variable)} className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg" data-id="element-2085">
                              <code className="text-blue-600" data-id="element-2086">
                                {v.variable}
                              </code>
                              <span className="block text-xs text-gray-500" data-id="element-2087">
                                {v.description}
                              </span>
                            </button>)}
                        </div>
                      </div>
                    </div>
                    <div ref={emailEditorRef} contentEditable className="min-h-[200px] p-4 focus:outline-none prose prose-sm max-w-none [&_a]:text-blue-600 [&_a]:underline [&_a]:cursor-pointer" style={{
                  whiteSpace: 'pre-wrap'
                }} onMouseMove={handleEditorMouseMove} onMouseLeave={handleEditorMouseLeave} onPaste={handleEditorPaste} suppressContentEditableWarning data-id="element-2088" />
                  </div>

                  {/* Link URL Tooltip */}
                  {linkTooltip && <div className="fixed z-50 px-2 py-1 text-xs bg-gray-900 text-white rounded shadow-lg max-w-xs truncate" style={{
                left: linkTooltip.x,
                top: linkTooltip.y
              }} data-id="element-2089">
                      {linkTooltip.url}
                    </div>}
                </div>
              </div>
            </Card>

            {/* SMS Template */}
            <Card data-id="element-2090">
              <div className="space-y-4" data-id="element-2091">
                <div className="flex items-center gap-2" data-id="element-2092">
                  <MessageSquareIcon className="w-5 h-5 text-gray-400" data-id="element-2093" />
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-2094">
                    Invoice SMS Template
                  </h3>
                </div>
                <p className="text-sm text-gray-500" data-id="element-2095">
                  This template will be used when sending invoices via SMS. Keep
                  it short and concise.
                </p>

                {/* Variable buttons for SMS */}
                <div className="flex flex-wrap gap-2" data-id="element-2096">
                  {templateVariables.map(v => <button key={v.variable} type="button" onClick={() => insertSmsVariable(v.variable)} className="px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs rounded font-mono transition-colors" title={`Insert ${v.description}`} data-id="element-2097">
                      + {v.variable}
                    </button>)}
                </div>

                <TextArea value={invoiceSmsTemplate} onChange={e => setInvoiceSmsTemplate(e.target.value)} placeholder="Hi %customer_name%, your invoice #%invoice_number% for %invoice_amount% is ready. View it here: %invoice_link%" className="min-h-[120px] font-mono text-sm" data-id="element-2098" />
                <p className="text-xs text-gray-500" data-id="element-2099">
                  Character count: {invoiceSmsTemplate.length} (SMS messages are
                  typically limited to 160 characters)
                </p>
              </div>
            </Card>

            {companyErrors.submit && <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-2100">
                <p className="text-sm text-red-600" data-id="element-2101">{companyErrors.submit}</p>
              </div>}

            {/* Action Buttons */}
            <div className="flex justify-end gap-3" data-id="element-2102">
              <Button variant="secondary" onClick={closeEditCompany} disabled={isSavingCompany} data-id="element-2103">
                Cancel
              </Button>
              <Button onClick={handleSaveCompany} isLoading={isSavingCompany} data-id="element-2104">
                Save Changes
              </Button>
            </div>
          </div>}

        {activeTab === 'subscription_templates' && <>
            {showCreateSubTemplate || editingSubTemplateId ? <div className="space-y-6" data-id="element-2105">
                {/* Loading state for edit */}
                {isLoadingEditSubTemplate ? <Card data-id="element-2106">
                    <div className="p-12 text-center" data-id="element-2107">
                      <div className="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" data-id="element-2108"></div>
                      <p className="mt-4 text-sm text-gray-500" data-id="element-2109">
                        Loading template details...
                      </p>
                    </div>
                  </Card> : editSubTemplateError ? <Card data-id="element-2110">
                    <div className="p-12 text-center" data-id="element-2111">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mb-4" data-id="element-2112">
                        <AlertCircleIcon className="w-6 h-6 text-red-600" data-id="element-2113" />
                      </div>
                      <p className="text-sm font-medium text-gray-900 mb-1" data-id="element-2114">
                        Failed to load template
                      </p>
                      <p className="text-sm text-gray-500 mb-4" data-id="element-2115">
                        {editSubTemplateError}
                      </p>
                      <div className="flex justify-center gap-3" data-id="element-2116">
                        <Button variant="secondary" size="sm" onClick={closeEditSubTemplate} data-id="element-2117">
                          Back to List
                        </Button>
                        <Button size="sm" onClick={() => editingSubTemplateId && openEditSubTemplate(editingSubTemplateId)} data-id="element-2118">
                          Retry
                        </Button>
                      </div>
                    </div>
                  </Card> : <>
                    {/* Back to list */}
                    <div className="flex items-center gap-3" data-id="element-2119">
                      <Button variant="ghost" size="sm" onClick={() => {
                if (editingSubTemplateId) {
                  closeEditSubTemplate();
                } else {
                  closeCreateSubTemplate();
                }
              }} leftIcon={<ArrowLeftIcon className="w-4 h-4" data-id="element-2121" />} data-id="element-2120">
                        Back to Templates
                      </Button>
                      <h2 className="text-lg font-semibold text-gray-900" data-id="element-2122">
                        {editingSubTemplateId ? 'Edit Subscription Template' : 'New Subscription Template'}
                      </h2>
                    </div>

                    {/* Template Details */}
                    <Card data-id="element-2123">
                      <CardHeader title="Template Details" data-id="element-2124" />
                      <div className="space-y-4" data-id="element-2125">
                        <Input label="Template Name" value={subTemplateName} onChange={e => setSubTemplateName(e.target.value)} error={subTemplateErrors.name} placeholder="e.g., Monthly Service Plan" data-id="element-2126" />
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-2127">
                          <div className="grid grid-cols-2 gap-2" data-id="element-2128">
                            <Input label="Every" type="number" min="1" value={subTemplateCycleNum} onChange={e => setSubTemplateCycleNum(Math.max(1, parseInt(e.target.value) || 1))} data-id="element-2129" />
                            <Select label="Period" options={[{
                      value: 'week',
                      label: 'Week(s)'
                    }, {
                      value: 'month',
                      label: 'Month(s)'
                    }, {
                      value: 'year',
                      label: 'Year(s)'
                    }]} value={subTemplateCycle} onChange={e => setSubTemplateCycle(e.target.value)} data-id="element-2130" />
                          </div>
                          <Select label="Installments" options={[{
                    value: '0',
                    label: 'Infinite'
                  }, {
                    value: '1',
                    label: '1'
                  }, {
                    value: '2',
                    label: '2'
                  }, {
                    value: '3',
                    label: '3'
                  }, {
                    value: '4',
                    label: '4'
                  }, {
                    value: '5',
                    label: '5'
                  }, {
                    value: '6',
                    label: '6'
                  }, {
                    value: '7',
                    label: '7'
                  }, {
                    value: '8',
                    label: '8'
                  }, {
                    value: '9',
                    label: '9'
                  }, {
                    value: '10',
                    label: '10'
                  }, {
                    value: '11',
                    label: '11'
                  }, {
                    value: '12',
                    label: '12'
                  }]} value={subTemplateInstallments} onChange={e => setSubTemplateInstallments(e.target.value)} helperText="Number of billing cycles (Infinite = ongoing)" data-id="element-2131" />
                        </div>
                      </div>
                    </Card>

                    {/* Line Items */}
                    <Card data-id="element-2132">
                      <CardHeader title="Recurring Items" subtitle="These items will be billed each cycle" action={<Button variant="secondary" size="sm" onClick={addSubTemplateItem} leftIcon={<PlusIcon className="w-4 h-4" data-id="element-2135" />} data-id="element-2134">
                            Add Item
                          </Button>} data-id="element-2133" />
                      {subTemplateErrors.items && <p className="text-sm text-red-600 mb-4" data-id="element-2136">
                          {subTemplateErrors.items}
                        </p>}
                      <div className="space-y-4" data-id="element-2137">
                        {subTemplateItems.map((item, index) => <div key={index} className="bg-gray-50 rounded-lg p-4 space-y-4" data-id="element-2138">
                            <div className="flex items-start justify-between gap-4" data-id="element-2139">
                              <span className="text-sm font-medium text-gray-500 mt-2" data-id="element-2140">
                                #{index + 1}
                              </span>
                              <div className="flex-1" data-id="element-2141">
                                <Input placeholder="Item description" value={item.description} onChange={e => updateSubTemplateItem(index, {
                        description: e.target.value
                      })} data-id="element-2142" />
                              </div>
                              {subTemplateItems.length > 1 && <Button variant="ghost" size="sm" onClick={() => removeSubTemplateItem(index)} className="text-red-600 hover:text-red-700 hover:bg-red-50" aria-label="Remove item" data-id="element-2143">
                                  <TrashIcon className="w-4 h-4" data-id="element-2144" />
                                </Button>}
                            </div>
                            <div className="grid grid-cols-3 gap-4" data-id="element-2145">
                              <Input label="Quantity" type="number" min="1" step="1" value={item.quantity} onChange={e => updateSubTemplateItem(index, {
                      quantity: Math.max(1, parseInt(e.target.value) || 1)
                    })} data-id="element-2146" />
                              <Input label="Unit Price" type="number" min="0" step="0.01" value={item.price} onChange={e => updateSubTemplateItem(index, {
                      price: Math.max(0, parseFloat(e.target.value) || 0)
                    })} data-id="element-2147" />
                              <div className="flex flex-col justify-end" data-id="element-2148">
                                <Checkbox label="Taxable" checked={item.isTaxable} onChange={e => updateSubTemplateItem(index, {
                        isTaxable: e.target.checked
                      })} data-id="element-2149" />
                              </div>
                            </div>
                            <div className="flex items-center pt-2 border-t border-gray-200 text-sm" data-id="element-2150">
                              <span className="text-gray-500" data-id="element-2151">Line Total:</span>
                              <span className="font-semibold text-gray-900 ml-2" data-id="element-2152">
                                {formatCurrency(item.quantity * item.price)}
                              </span>
                            </div>
                          </div>)}
                      </div>
                    </Card>

                    {/* Tax & Fee Settings */}
                    <Card data-id="element-2153">
                      <CardHeader title="Tax & Fee Settings" data-id="element-2154" />
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2155">
                        <Input label="Tax Label" value={subTemplateTaxLabel} onChange={e => setSubTemplateTaxLabel(e.target.value)} placeholder="e.g., Tax (6%)" helperText={`Company tax rate (${Math.round(companyTaxRate)}%) applied to taxable items`} data-id="element-2156" />
                        <Input label="Fee" type="number" min="0" step="0.01" value={subTemplateFee} onChange={e => setSubTemplateFee(parseFloat(e.target.value) || 0)} helperText="Additional fee charged each billing cycle" data-id="element-2157" />
                        <Input label="Fee Label" value={subTemplateFeeLabel} onChange={e => setSubTemplateFeeLabel(e.target.value)} placeholder="e.g., Service Fee" data-id="element-2158" />
                      </div>
                    </Card>

                    {/* Summary */}
                    <Card data-id="element-2159">
                      <CardHeader title="Per-Cycle Summary" data-id="element-2160" />
                      <div className="space-y-2 text-sm" data-id="element-2161">
                        <div className="flex justify-between" data-id="element-2162">
                          <span className="text-gray-500" data-id="element-2163">Subtotal</span>
                          <span className="text-gray-900" data-id="element-2164">
                            {formatCurrency(subTemplateItems.reduce((sum, item) => sum + item.quantity * item.price, 0))}
                          </span>
                        </div>
                        {companyTaxRate > 0 && <div className="flex justify-between" data-id="element-2165">
                            <span className="text-gray-500" data-id="element-2166">
                              {subTemplateTaxLabel || 'Tax'}
                            </span>
                            <span className="text-gray-900" data-id="element-2167">
                              {formatCurrency(subTemplateItems.filter(item => item.isTaxable).reduce((sum, item) => sum + item.quantity * item.price, 0) * (companyTaxRate / 100))}
                            </span>
                          </div>}
                        {subTemplateFee > 0 && <div className="flex justify-between" data-id="element-2168">
                            <span className="text-gray-500" data-id="element-2169">
                              {subTemplateFeeLabel || 'Fee'}
                            </span>
                            <span className="text-gray-900" data-id="element-2170">
                              {formatCurrency(subTemplateFee)}
                            </span>
                          </div>}
                        <div className="flex justify-between pt-2 border-t border-gray-200 font-semibold" data-id="element-2171">
                          <span className="text-gray-900" data-id="element-2172">Total</span>
                          <span className="text-gray-900" data-id="element-2173">
                            {formatCurrency(subTemplateItems.reduce((sum, item) => sum + item.quantity * item.price, 0) + subTemplateItems.filter(item => item.isTaxable).reduce((sum, item) => sum + item.quantity * item.price, 0) * (companyTaxRate / 100) + subTemplateFee)}
                          </span>
                        </div>
                      </div>
                    </Card>

                    {/* Error */}
                    {subTemplateErrors.submit && <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-2174">
                        <p className="text-sm text-red-600" data-id="element-2175">
                          {subTemplateErrors.submit}
                        </p>
                      </div>}

                    {/* Save Button */}
                    <div className="flex justify-end gap-3" data-id="element-2176">
                      <Button variant="secondary" onClick={() => {
                if (editingSubTemplateId) {
                  closeEditSubTemplate();
                } else {
                  closeCreateSubTemplate();
                }
              }} disabled={isSavingSubTemplate} data-id="element-2177">
                        Cancel
                      </Button>
                      <Button onClick={editingSubTemplateId ? handleUpdateSubTemplate : handleSaveSubTemplate} isLoading={isSavingSubTemplate} leftIcon={<SaveIcon className="w-4 h-4" data-id="element-2179" />} data-id="element-2178">
                        {editingSubTemplateId ? 'Save Changes' : 'Save Template'}
                      </Button>
                    </div>
                  </>}
              </div> : <Card padding="none" data-id="element-2180">
                <div className="p-6 border-b border-gray-200" data-id="element-2181">
                  <CardHeader title="Subscription Templates" subtitle="Manage reusable subscription templates with shareable links" action={<Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-2184" />} onClick={openCreateSubTemplate} data-id="element-2183">
                        Add Subscription Template
                      </Button>} data-id="element-2182" />
                </div>

                {isLoadingSubTemplates ? <div className="p-12 text-center" data-id="element-2185">
                    <div className="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" data-id="element-2186"></div>
                    <p className="mt-4 text-sm text-gray-500" data-id="element-2187">
                      Loading subscription templates...
                    </p>
                  </div> : subTemplatesError ? <div className="p-12 text-center" data-id="element-2188">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mb-4" data-id="element-2189">
                      <AlertCircleIcon className="w-6 h-6 text-red-600" data-id="element-2190" />
                    </div>
                    <p className="text-sm font-medium text-gray-900 mb-1" data-id="element-2191">
                      Failed to load subscription templates
                    </p>
                    <p className="text-sm text-gray-500 mb-4" data-id="element-2192">
                      {subTemplatesError}
                    </p>
                    <Button size="sm" onClick={() => editingCompany && fetchSubscriptionTemplates(editingCompany.id)} data-id="element-2193">
                      Retry
                    </Button>
                  </div> : <Table columns={subscriptionTemplateColumns} data={subscriptionTemplates} keyExtractor={t => t.id} emptyMessage="No subscription templates found" data-id="element-2194" />}
              </Card>}
          </>}

        {/* Share Link Modal */}
        <Modal isOpen={shareModalOpen} onClose={() => setShareModalOpen(false)} title="Share Subscription Template" data-id="element-2195">
          <div className="space-y-4" data-id="element-2196">
            <p className="text-sm text-gray-600" data-id="element-2197">
              Share this subscription template using the link below:
            </p>

            {shareLink && <div className="p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-2198">
                <code className="text-xs text-gray-700 break-all" data-id="element-2199">
                  {shareLink}
                </code>
              </div>}

            <div className="flex gap-3 pt-2" data-id="element-2200">
              <Button variant="primary" onClick={handleCopyLink} className="flex-1" data-id="element-2201">
                {copySuccess ? <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-2202">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-2203" />
                    </svg>
                    Copied!
                  </> : <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-2204">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" data-id="element-2205" />
                    </svg>
                    Copy Link
                  </>}
              </Button>

              <Button variant="secondary" onClick={handleViewLink} className="flex-1" data-id="element-2206">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-2207">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" data-id="element-2208" />
                </svg>
                View
              </Button>
            </div>
          </div>
        </Modal>

        {/* Delete Subscription Template Confirmation Modal */}
        <Modal isOpen={!!deleteSubTemplateId} onClose={() => setDeleteSubTemplateId(null)} title="Delete Subscription Template" size="sm" data-id="element-2209">
          <p className="text-gray-600 mb-6" data-id="element-2210">
            Are you sure you want to delete this subscription template? This
            action cannot be undone.
          </p>
          <div className="flex justify-end gap-3" data-id="element-2211">
            <Button variant="secondary" onClick={() => setDeleteSubTemplateId(null)} data-id="element-2212">
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeleteSubTemplate} isLoading={isDeletingSubTemplate} data-id="element-2213">
              Delete
            </Button>
          </div>
        </Modal>

        {/* Run Subscription Template Modal */}
        <Modal isOpen={!!runTemplateId} onClose={() => {
        setRunTemplateId(null);
        setRunTemplateError(null);
      }} title="Create Subscription from Template" data-id="element-2214">
          <div className="space-y-4" data-id="element-2215">
            <p className="text-sm text-gray-600" data-id="element-2216">
              Select a customer to create a new subscription from this template.
            </p>

            <CustomerSelect value={runTemplateCustomerId} onChange={customerId => {
            setRunTemplateCustomerId(customerId);
            setRunTemplateError(null);
          }} error={runTemplateError && !runTemplateCustomerId ? runTemplateError : undefined} companyId={editingCompany?.id || selectedCompanyId} data-id="element-2217" />

            <Input label="Start Date" type="date" value={runTemplateStartDate} onChange={e => {
            setRunTemplateStartDate(e.target.value);
            setRunTemplateError(null);
          }} min={(() => {
            const today = new Date();
            const yyyy = today.getFullYear();
            const mm = String(today.getMonth() + 1).padStart(2, '0');
            const dd = String(today.getDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
          })()} data-id="element-2218" />

            {/* Payment Methods */}
            {runTemplateCustomerId && <div data-id="element-2219">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2220">
                  Payment Method
                </label>
                {isLoadingRunTemplatePaymentMethods ? <div className="text-center py-4" data-id="element-2221">
                    <div className="inline-block w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mb-1" data-id="element-2222"></div>
                    <p className="text-xs text-gray-500" data-id="element-2223">
                      Loading payment methods...
                    </p>
                  </div> : runTemplatePaymentMethods.length === 0 ? <div className="text-center py-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-2224">
                    <CreditCardIcon className="w-6 h-6 text-gray-300 mx-auto mb-1" data-id="element-2225" />
                    <p className="text-xs text-gray-500" data-id="element-2226">
                      No saved payment methods
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5" data-id="element-2227">
                      Invoice will be created without auto-charge
                    </p>
                  </div> : <div className="space-y-2" data-id="element-2228">
                    {runTemplatePaymentMethods.map(method => {
                const brand = getRunTemplateCardBrandDisplay(method.card_brand);
                const isSelected = runTemplateSelectedPaymentMethodId === method.id;
                return <div key={method.id} onClick={() => setRunTemplateSelectedPaymentMethodId(method.id)} className={`border rounded-lg p-3 transition-colors cursor-pointer ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2229">
                          <div className="flex items-center gap-3" data-id="element-2230">
                            <div className={`w-10 h-6 ${brand.color} rounded flex items-center justify-center text-white text-xs font-bold`} data-id="element-2231">
                              {brand.name.slice(0, 4)}
                            </div>
                            <div className="flex-1" data-id="element-2232">
                              <span className="text-sm text-gray-900" data-id="element-2233">
                                •••• {method.card_last_digits}
                              </span>
                              <p className="text-xs text-gray-500" data-id="element-2234">
                                Expires {method.card_exp}
                              </p>
                            </div>
                            {isSelected && <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center" data-id="element-2235">
                                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-2236">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-2237" />
                                </svg>
                              </div>}
                          </div>
                        </div>;
              })}
                    {/* "Don't pay first invoice" option - only when start date is not future */}
                    {!isRunTemplateStartDateFuture && <div onClick={() => setRunTemplateSelectedPaymentMethodId(SKIP_PAYMENT_ID)} className={`border rounded-lg p-3 transition-colors cursor-pointer ${runTemplateSelectedPaymentMethodId === SKIP_PAYMENT_ID ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2238">
                        <div className="flex items-center gap-3" data-id="element-2239">
                          <div className="w-10 h-6 bg-amber-500 rounded flex items-center justify-center" data-id="element-2240">
                            <XCircleIcon className="w-4 h-4 text-white" data-id="element-2241" />
                          </div>
                          <div className="flex-1" data-id="element-2242">
                            <span className="text-sm text-gray-900" data-id="element-2243">
                              Don't pay the first invoice right now
                            </span>
                            <p className="text-xs text-gray-500" data-id="element-2244">
                              Invoice will be created but not charged
                            </p>
                          </div>
                          {runTemplateSelectedPaymentMethodId === SKIP_PAYMENT_ID && <div className="w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center" data-id="element-2245">
                              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-2246">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-2247" />
                              </svg>
                            </div>}
                        </div>
                      </div>}
                  </div>}
              </div>}

            {runTemplateError && runTemplateCustomerId && <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-2248">
                <p className="text-sm text-red-600" data-id="element-2249">{runTemplateError}</p>
              </div>}

            <div className="flex justify-end gap-3 pt-2" data-id="element-2250">
              <Button variant="secondary" onClick={() => {
              setRunTemplateId(null);
              setRunTemplateError(null);
            }} data-id="element-2251">
                Cancel
              </Button>
              <Button onClick={handleRunTemplate} isLoading={isRunningTemplate} disabled={!runTemplateCustomerId} leftIcon={<PlayIcon className="w-4 h-4" data-id="element-2253" />} data-id="element-2252">
                Create Subscription
              </Button>
            </div>
          </div>
        </Modal>

        {/* Run Template Success Modal */}
        <Modal isOpen={!!runTemplateSuccessNumber} onClose={() => setRunTemplateSuccessNumber(null)} title="Subscription Created" size="sm" data-id="element-2254">
          <div className="text-center space-y-4" data-id="element-2255">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mx-auto" data-id="element-2256">
              <CheckCircleIcon className="w-6 h-6 text-green-600" data-id="element-2257" />
            </div>
            <div data-id="element-2258">
              <p className="text-gray-900 font-medium" data-id="element-2259">
                Subscription created successfully!
              </p>
              <p className="text-sm text-gray-500 mt-1" data-id="element-2260">
                Subscription number:{' '}
                <span className="font-semibold text-gray-900" data-id="element-2261">
                  {runTemplateSuccessNumber}
                </span>
              </p>
            </div>
            <div className="pt-2" data-id="element-2262">
              <Button onClick={() => setRunTemplateSuccessNumber(null)} data-id="element-2263">
                OK
              </Button>
            </div>
          </div>
        </Modal>
      </div>;
  }
  return <div className="space-y-6" data-id="element-2264">
      {/* Page Header */}
      <div data-id="element-2265">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-2266">Settings</h1>
        <p className="text-gray-500 mt-1" data-id="element-2267">
          Manage your billing system configuration
        </p>
      </div>

      {/* Companies */}
      <Card padding="none" data-id="element-2268">
        <div className="p-6 border-b border-gray-200" data-id="element-2269">
          <CardHeader title="Companies" subtitle="Manage company information, tax rates, and Stripe integration" data-id="element-2270" />
        </div>

        {isLoadingCompanies ? <div className="p-12 text-center" data-id="element-2271">
            <div className="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" data-id="element-2272"></div>
            <p className="mt-4 text-sm text-gray-500" data-id="element-2273">Loading companies...</p>
          </div> : companiesError ? <div className="p-12 text-center" data-id="element-2274">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mb-4" data-id="element-2275">
              <AlertCircleIcon className="w-6 h-6 text-red-600" data-id="element-2276" />
            </div>
            <p className="text-sm font-medium text-gray-900 mb-1" data-id="element-2277">
              Failed to load companies
            </p>
            <p className="text-sm text-gray-500 mb-4" data-id="element-2278">{companiesError}</p>
            <Button size="sm" onClick={() => fetchCompanies()} data-id="element-2279">
              Retry
            </Button>
          </div> : <Table columns={companyColumns} data={companies} keyExtractor={c => c.id} emptyMessage="No companies found" data-id="element-2280" />}
      </Card>

      {/* Terms & Conditions */}
      <Card padding="none" data-id="element-2281">
        <div className="p-6 border-b border-gray-200" data-id="element-2282">
          <CardHeader title="Terms & Conditions" subtitle="Manage terms and conditions templates for quotes" action={<Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-2285" />} onClick={openCreateTermsModal} data-id="element-2284">
                Add Terms
              </Button>} data-id="element-2283" />
        </div>
        <Table columns={termsColumns} data={companyTerms} keyExtractor={t => t.id} emptyMessage="No terms and conditions created yet" data-id="element-2286" />
      </Card>

      {/* System Info */}
      <Card data-id="element-2287">
        <CardHeader title="System Information" data-id="element-2288" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm" data-id="element-2289">
          <div data-id="element-2290">
            <span className="text-gray-500" data-id="element-2291">Version</span>
            <p className="font-medium text-gray-900" data-id="element-2292">1.0.0</p>
          </div>
          <div data-id="element-2293">
            <span className="text-gray-500" data-id="element-2294">Environment</span>
            <p className="font-medium text-gray-900" data-id="element-2295">Production</p>
          </div>
          <div data-id="element-2296">
            <span className="text-gray-500" data-id="element-2297">Stripe Fee</span>
            <p className="font-medium text-gray-900" data-id="element-2298">2.9% + $0.30</p>
          </div>
          <div data-id="element-2299">
            <span className="text-gray-500" data-id="element-2300">Data Storage</span>
            <p className="font-medium text-gray-900" data-id="element-2301">API Database</p>
          </div>
        </div>
      </Card>

      {/* Terms Modal */}
      <Modal isOpen={isTermsModalOpen} onClose={() => setIsTermsModalOpen(false)} title={editingTerms ? 'Edit Terms & Conditions' : 'Add Terms & Conditions'} size="lg" data-id="element-2302">
        <div className="space-y-4" data-id="element-2303">
          <Input label="Name" value={termsName} onChange={e => setTermsName(e.target.value)} error={termsErrors.name} placeholder="e.g., Standard Terms, Service Agreement" data-id="element-2304" />

          <TextArea label="Content" value={termsContent} onChange={e => setTermsContent(e.target.value)} error={termsErrors.content} placeholder="Enter your terms and conditions..." className="min-h-[200px]" helperText="You can use basic HTML formatting" data-id="element-2305" />

          <Checkbox label="Set as default terms" checked={isDefault} onChange={e => setIsDefault(e.target.checked)} data-id="element-2306" />

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200" data-id="element-2307">
            <Button variant="secondary" onClick={() => setIsTermsModalOpen(false)} data-id="element-2308">
              Cancel
            </Button>
            <Button onClick={handleSaveTerms} isLoading={isLoading} data-id="element-2309">
              {editingTerms ? 'Save Changes' : 'Add Terms'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={!!deleteConfirmId} onClose={() => setDeleteConfirmId(null)} title="Delete Terms & Conditions" size="sm" data-id="element-2310">
        <p className="text-gray-600 mb-6" data-id="element-2311">
          Are you sure you want to delete these terms? This action cannot be
          undone.
        </p>
        <div className="flex justify-end gap-3" data-id="element-2312">
          <Button variant="secondary" onClick={() => setDeleteConfirmId(null)} data-id="element-2313">
            Cancel
          </Button>
          <Button variant="danger" onClick={() => deleteConfirmId && handleDeleteTerms(deleteConfirmId)} isLoading={isLoading} data-id="element-2314">
            Delete
          </Button>
        </div>
      </Modal>
    </div>;
}